import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Statistic,
  Spin, message, Table, Modal, Form, Input, DatePicker, TimePicker,
  Tabs, Badge, Tooltip, Alert, Popover, Divider
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  CompassOutlined,
  CoffeeOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import StaffLayout from './StaffLayout';
import api from '../api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const dayStatusColors = {
  PRESENT: { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d', badgeBg: '#dcfce7', label: 'Present', dot: '#22c55e' },
  HALF_DAY: { bg: '#fffbeb', border: '#fde68a', text: '#b45309', badgeBg: '#fef3c7', label: 'Half Day', dot: '#f59e0b' },
  ABSENT: { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', badgeBg: '#fee2e2', label: 'Absent', dot: '#ef4444' },
  LEAVE: { bg: '#fefce8', border: '#fef08a', text: '#a16207', badgeBg: '#fef9c3', label: 'Leave', dot: '#eab308' },
  WEEKLY_OFF: { bg: '#f0f9ff', border: '#bae6fd', text: '#0369a1', badgeBg: '#e0f2fe', label: 'Weekly Off', dot: '#0ea5e9' },
  HOLIDAY: { bg: '#faf5ff', border: '#e9d5ff', text: '#6d28d9', badgeBg: '#f3e8ff', label: 'Holiday', dot: '#8b5cf6' },
  NA: { bg: '#f8fafc', border: '#e2e8f0', text: '#64748b', badgeBg: '#f1f5f9', label: 'Upcoming', dot: '#94a3b8' },
  FUTURE: { bg: '#f8fafc', border: '#e2e8f0', text: '#64748b', badgeBg: '#f1f5f9', label: 'Upcoming', dot: '#94a3b8' },
};

const StaffAttendance = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState(null);
  const [daysMap, setDaysMap] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  // Punch history state
  const [todayStatus, setTodayStatus] = useState(null);

  // Regularization state
  const [regularizations, setRegularizations] = useState([]);
  const [regModalVisible, setRegModalVisible] = useState(false);
  const [submittingReg, setSubmittingReg] = useState(false);
  const [regForm] = Form.useForm();

  // Load monthly attendance history
  const loadAttendanceHistory = async () => {
    setLoading(true);
    try {
      const monthKey = selectedMonth.format('YYYY-MM');
      const [histRes, statRes, regRes] = await Promise.all([
        api.get(`/attendance/history?month=${monthKey}`).catch(() => ({ data: null })),
        api.get('/attendance/status').catch(() => ({ data: null })),
        api.get(`/attendance/regularization/my?month=${monthKey}`).catch(() => ({ data: null }))
      ]);

      if (histRes.data?.success) {
        setHistoryData(histRes.data);
        const map = {};
        (histRes.data.days || []).forEach(d => {
          if (d.date) map[d.date] = d;
        });
        setDaysMap(map);
      }

      if (statRes.data?.success) {
        setTodayStatus(statRes.data.status);
      }

      if (regRes.data?.success) {
        setRegularizations(regRes.data.regularizations || []);
      }
    } catch (err) {
      console.error('Failed to load attendance details:', err);
      message.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendanceHistory();
  }, [selectedMonth]);

  // Calendar matrix calculation
  const calendarGrid = useMemo(() => {
    const year = selectedMonth.year();
    const month = selectedMonth.month(); // 0-indexed
    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 is Sunday
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDayIndex; i++) {
      cells.push({ isEmpty: true, key: `empty-start-${i}` });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({
        isEmpty: false,
        dayNum: d,
        date: dateStr,
        key: dateStr
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ isEmpty: true, key: `empty-end-${cells.length}` });
    }
    return cells;
  }, [selectedMonth]);

  // Navigation handlers
  const handlePrevMonth = () => setSelectedMonth(prev => prev.subtract(1, 'month'));
  const handleNextMonth = () => setSelectedMonth(prev => prev.add(1, 'month'));

  // Regularization submit
  const handleApplyRegularization = async (values) => {
    setSubmittingReg(true);
    try {
      const payload = {
        date: values.date.format('YYYY-MM-DD'),
        requestedInTime: values.inTime ? values.inTime.format('HH:mm:ss') : null,
        requestedOutTime: values.outTime ? values.outTime.format('HH:mm:ss') : null,
        reason: values.reason
      };

      const res = await api.post('/attendance/regularization', payload);
      if (res.data?.success) {
        message.success('Attendance regularization requested successfully!');
        setRegModalVisible(false);
        regForm.resetFields();
        loadAttendanceHistory();
      } else {
        message.error(res.data?.message || 'Failed to submit regularization');
      }
    } catch (err) {
      console.error('Regularization submit error:', err);
      message.error(err.response?.data?.message || 'Error submitting regularization request');
    } finally {
      setSubmittingReg(false);
    }
  };

  const summary = historyData?.summary || {
    present: 0,
    halfDay: 0,
    absent: 0,
    leave: 0,
    weeklyOff: 0,
    holiday: 0,
    lateCount: 0,
    overtime: 0
  };

  return (
    <StaffLayout>
      <style>{`
        .calendar-day-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
        }
      `}</style>

      {/* 1. MONTH HEADER & SUMMARY STATS */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          marginBottom: 24,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          <Space align="center" size={12}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb',
              fontSize: 22
            }}>
              <CalendarOutlined />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                {monthNames[selectedMonth.month()]} {selectedMonth.year()}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>Monthly Attendance Overview & Logs</Text>
            </div>
          </Space>

          <Space size={12} wrap>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(val) => { if (val) setSelectedMonth(val); }}
              allowClear={false}
              format="MMM YYYY"
              style={{ borderRadius: 8, width: 130 }}
            />

            <Button.Group>
              <Button icon={<LeftOutlined />} onClick={handlePrevMonth} title="Previous Month" />
              <Button onClick={() => setSelectedMonth(dayjs())}>Today</Button>
              <Button icon={<RightOutlined />} onClick={handleNextMonth} title="Next Month" />
            </Button.Group>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                regForm.resetFields();
                setRegModalVisible(true);
              }}
              style={{
                borderRadius: 8,
                background: '#2563eb',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
              }}
            >
              Apply Regularization
            </Button>
          </Space>
        </div>

        {/* 7 Metric Cards (Payable Days removed) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 12
        }}>
          {[
            { label: 'Present', value: summary.present, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: <CheckCircleOutlined /> },
            { label: 'Half Day', value: summary.halfDay, color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: <ClockCircleOutlined /> },
            { label: 'Absent', value: summary.absent, color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: <CloseCircleOutlined /> },
            { label: 'Leaves', value: summary.leave, color: '#ca8a04', bg: '#fefce8', border: '#fef08a', icon: <CoffeeOutlined /> },
            { label: 'Weekly Off', value: summary.weeklyOff, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: <CalendarOutlined /> },
            { label: 'Holidays', value: summary.holiday, color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff', icon: <ThunderboltOutlined /> },
            { label: 'Late Days', value: summary.lateCount || 0, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', icon: <ExclamationCircleOutlined /> }
          ].map(m => (
            <div
              key={m.label}
              style={{
                background: m.bg,
                border: `1px solid ${m.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.label}</span>
                <span style={{ fontSize: 16, color: m.color, opacity: 0.85 }}>{m.icon}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 2. TABS: Attendance Calendar vs Punch History vs Regularization Requests */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}
        bodyStyle={{ padding: 24 }}
      >
        <Tabs
          defaultActiveKey="calendar"
          items={[
            {
              key: 'calendar',
              label: (
                <span>
                  <CalendarOutlined />
                  Interactive Calendar
                </span>
              ),
              children: (
                <div>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                      <Spin size="large" tip="Loading calendar records..." />
                    </div>
                  ) : (
                    <div>
                      {/* Visual Color Legend Bar */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        padding: '12px 16px',
                        background: '#f8fafc',
                        borderRadius: 12,
                        marginBottom: 16,
                        border: '1px solid #f1f5f9'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CalendarOutlined style={{ color: '#2563eb' }} />
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Status Legend:</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                          {[
                            { label: 'Present', dot: '#22c55e', text: '#15803d' },
                            { label: 'Half Day', dot: '#f59e0b', text: '#b45309' },
                            { label: 'Absent', dot: '#ef4444', text: '#b91c1c' },
                            { label: 'Leave', dot: '#eab308', text: '#a16207' },
                            { label: 'Weekly Off', dot: '#0ea5e9', text: '#0369a1' },
                            { label: 'Holiday', dot: '#8b5cf6', text: '#6d28d9' },
                            { label: 'Upcoming', dot: '#94a3b8', text: '#64748b' }
                          ].map(item => (
                            <div key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: item.text, fontWeight: 500 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.dot, display: 'inline-block' }} />
                              <span>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Weekday Header */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 8,
                        textAlign: 'center',
                        marginBottom: 10
                      }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, idx) => (
                          <div
                            key={d}
                            style={{
                              padding: '10px 0',
                              background: idx === 0 || idx === 6 ? '#f1f5f9' : '#f8fafc',
                              borderRadius: 8,
                              fontWeight: 700,
                              fontSize: 12,
                              color: idx === 0 ? '#ef4444' : (idx === 6 ? '#2563eb' : '#475569'),
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em'
                            }}
                          >
                            {d}
                          </div>
                        ))}
                      </div>

                      {/* Day Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 8
                      }}>
                        {calendarGrid.map((cell) => {
                          if (cell.isEmpty) {
                            return (
                              <div
                                key={cell.key}
                                style={{
                                  minHeight: 104,
                                  background: '#fafafa',
                                  borderRadius: 12,
                                  border: '1px dashed #f1f5f9',
                                  opacity: 0.35
                                }}
                              />
                            );
                          }

                          const todayStr = dayjs().format('YYYY-MM-DD');
                          const dRecord = daysMap[cell.date];
                          const isToday = cell.date === todayStr;
                          const isFuture = dayjs(cell.date).isAfter(dayjs(todayStr), 'day');

                          // Resolve status: do NOT falsely mark future days as ABSENT
                          let statusKey = 'NA';
                          if (dRecord?.dayStatus && dRecord.dayStatus !== 'NA') {
                            statusKey = dRecord.dayStatus;
                          } else if (isFuture) {
                            statusKey = 'NA';
                          } else {
                            statusKey = 'ABSENT';
                          }

                          const theme = dayStatusColors[statusKey] || dayStatusColors.NA;

                          return (
                            <div
                              key={cell.key}
                              onClick={() => setSelectedDay(dRecord || { date: cell.date, dayStatus: statusKey })}
                              style={{
                                minHeight: 104,
                                padding: '10px 10px',
                                borderRadius: 12,
                                background: isToday ? '#ffffff' : (isFuture && !dRecord ? '#fafbfc' : theme.bg),
                                border: isToday
                                  ? '2px solid #2563eb'
                                  : (isFuture && !dRecord ? '1px dashed #e2e8f0' : `1px solid ${theme.border}`),
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                boxShadow: isToday ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none'
                              }}
                              className="calendar-day-card"
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {isToday ? (
                                  <span style={{
                                    background: '#2563eb',
                                    color: '#ffffff',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}>
                                    <span>{cell.dayNum}</span>
                                    <span style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>Today</span>
                                  </span>
                                ) : (
                                  <span style={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: isFuture && !dRecord ? '#94a3b8' : '#0f172a'
                                  }}>
                                    {cell.dayNum}
                                  </span>
                                )}

                                {dRecord?.lateMinutes > 0 && (
                                  <span style={{
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    padding: '1px 5px',
                                    borderRadius: 6
                                  }}>
                                    {dRecord.lateMinutes}m Late
                                  </span>
                                )}
                              </div>

                              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  background: theme.badgeBg,
                                  color: theme.text,
                                  padding: '2px 6px',
                                  borderRadius: 6,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  width: 'fit-content'
                                }}>
                                  <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: theme.dot }} />
                                  <span>{theme.label}</span>
                                </div>

                                {dRecord?.punchedInAt && (
                                  <div style={{ fontSize: 10, color: '#475569', fontWeight: 500, marginTop: 2 }}>
                                    In: {dayjs(dRecord.punchedInAt).format('hh:mm A')}
                                  </div>
                                )}

                                {dRecord?.totalDurationSeconds > 0 && (
                                  <div style={{ fontSize: 10, color: '#64748b' }}>
                                    ⏱️ {Math.floor(dRecord.totalDurationSeconds / 3600)}h {Math.floor((dRecord.totalDurationSeconds % 3600) / 60)}m
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            },
            {
              key: 'history',
              label: (
                <span>
                  <HistoryOutlined />
                  Punch Logs & History
                </span>
              ),
              children: (
                <div>
                  <Title level={5} style={{ marginBottom: 16 }}>Today's Realtime Punch Logs</Title>
                  {(!todayStatus?.todayPunches || todayStatus.todayPunches.length === 0) ? (
                    <Alert
                      type="info"
                      message="No punches recorded for today yet."
                      showIcon
                      style={{ marginBottom: 20 }}
                    />
                  ) : (
                    <Table
                      dataSource={todayStatus.todayPunches}
                      rowKey="id"
                      pagination={false}
                      size="middle"
                      style={{ marginBottom: 24 }}
                      columns={[
                        {
                          title: 'Punch #',
                          dataIndex: 'punchNumber',
                          key: 'punchNumber',
                          render: (n) => <Tag color="blue">Punch #{n || 1}</Tag>
                        },
                        {
                          title: 'In Time',
                          dataIndex: 'punchedInAt',
                          key: 'punchedInAt',
                          render: (t) => t ? dayjs(t).format('hh:mm:ss A') : '--'
                        },
                        {
                          title: 'Out Time',
                          dataIndex: 'punchedOutAt',
                          key: 'punchedOutAt',
                          render: (t) => t ? dayjs(t).format('hh:mm:ss A') : <Tag color="green">Active</Tag>
                        },
                        {
                          title: 'Duration',
                          dataIndex: 'workMinutes',
                          key: 'workMinutes',
                          render: (m) => m ? `${m} mins` : '--'
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (s) => (
                            <Tag color={s === 'COMPLETED' ? 'green' : 'gold'}>
                              {s || 'ACTIVE'}
                            </Tag>
                          )
                        }
                      ]}
                    />
                  )}

                  <Divider />

                  <Title level={5} style={{ marginBottom: 16 }}>Monthly Punch Details</Title>
                  <Table
                    dataSource={historyData?.days || []}
                    rowKey="date"
                    pagination={{ pageSize: 10 }}
                    size="small"
                    columns={[
                      {
                        title: 'Date',
                        dataIndex: 'date',
                        key: 'date',
                        render: (d) => <Text strong>{d}</Text>
                      },
                      {
                        title: 'Status',
                        dataIndex: 'dayStatus',
                        key: 'dayStatus',
                        render: (s) => {
                          const col = {
                            PRESENT: 'green',
                            ABSENT: 'red',
                            HALF_DAY: 'orange',
                            WEEKLY_OFF: 'blue',
                            HOLIDAY: 'purple',
                            LEAVE: 'gold'
                          }[s] || 'default';
                          return <Tag color={col}>{s || 'ABSENT'}</Tag>;
                        }
                      },
                      {
                        title: 'Punch In',
                        dataIndex: 'punchedInAt',
                        key: 'punchedInAt',
                        render: (t) => t ? dayjs(t).format('hh:mm A') : '--'
                      },
                      {
                        title: 'Punch Out',
                        dataIndex: 'punchedOutAt',
                        key: 'punchedOutAt',
                        render: (t) => t ? dayjs(t).format('hh:mm A') : '--'
                      },
                      {
                        title: 'Work Hours',
                        dataIndex: 'totalDurationSeconds',
                        key: 'totalDurationSeconds',
                        render: (sec) => {
                          if (!sec) return '--';
                          const h = Math.floor(sec / 3600);
                          const m = Math.floor((sec % 3600) / 60);
                          return `${h}h ${m}m`;
                        }
                      },
                      {
                        title: 'Late (Mins)',
                        dataIndex: 'lateMinutes',
                        key: 'lateMinutes',
                        render: (m) => Number(m) > 0 ? <Tag color="volcano">{m}m late</Tag> : '--'
                      },
                      {
                        title: 'Overtime',
                        dataIndex: 'overtimeSeconds',
                        key: 'overtimeSeconds',
                        render: (sec) => {
                          if (!sec || Number(sec) <= 0) return '--';
                          const h = (Number(sec) / 3600).toFixed(1);
                          return <Tag color="green">+{h} hrs</Tag>;
                        }
                      }
                    ]}
                  />
                </div>
              )
            },
            {
              key: 'regularization',
              label: (
                <span>
                  <CheckCircleOutlined />
                  Regularization Requests
                  {regularizations.length > 0 && (
                    <Badge count={regularizations.length} style={{ marginLeft: 6, backgroundColor: '#2563eb' }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>Attendance Regularization</Title>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Forgot to punch in or faced technical glitch? Request punch regularization.
                      </Text>
                    </div>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        regForm.resetFields();
                        setRegModalVisible(true);
                      }}
                    >
                      New Request
                    </Button>
                  </div>

                  {regularizations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                      <CheckCircleOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                      <div>No regularization requests submitted for this month.</div>
                    </div>
                  ) : (
                    <Table
                      dataSource={regularizations}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      size="middle"
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (d) => <Text strong>{d}</Text>
                        },
                        {
                          title: 'Requested In Time',
                          dataIndex: 'requestedInTime',
                          key: 'requestedInTime',
                          render: (t) => t || '--'
                        },
                        {
                          title: 'Requested Out Time',
                          dataIndex: 'requestedOutTime',
                          key: 'requestedOutTime',
                          render: (t) => t || '--'
                        },
                        {
                          title: 'Reason',
                          dataIndex: 'reason',
                          key: 'reason'
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (s) => {
                            const col = s === 'APPROVED' ? 'green' : (s === 'REJECTED' ? 'red' : 'gold');
                            return <Tag color={col}>{s}</Tag>;
                          }
                        },
                        {
                          title: 'Admin Remarks',
                          dataIndex: 'adminRemarks',
                          key: 'adminRemarks',
                          render: (r) => r || '--'
                        }
                      ]}
                    />
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* 3. MODAL: Day Details Popup */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined style={{ color: '#2563eb' }} />
            <span>{selectedDay?.date ? dayjs(selectedDay.date).format('dddd, DD MMMM YYYY') : 'Day Details'}</span>
          </div>
        }
        open={!!selectedDay}
        onCancel={() => setSelectedDay(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedDay(null)}>
            Close
          </Button>,
          <Button
            key="regularize"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              const d = selectedDay?.date;
              setSelectedDay(null);
              regForm.setFieldsValue({ date: dayjs(d) });
              setRegModalVisible(true);
            }}
            style={{ background: '#2563eb' }}
          >
            Regularize This Day
          </Button>
        ]}
      >
        {selectedDay && (() => {
          const stTheme = dayStatusColors[selectedDay.dayStatus] || dayStatusColors.NA;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '10px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 13 }}>Day Status:</Text>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: stTheme.badgeBg,
                  color: stTheme.text,
                  padding: '3px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: stTheme.dot }} />
                  <span>{stTheme.label || selectedDay.dayStatus}</span>
                </div>
              </div>

              <Divider style={{ margin: '4px 0' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Punch In Time:</Text>
                <Text strong>{selectedDay.punchedInAt ? dayjs(selectedDay.punchedInAt).format('hh:mm A') : '--'}</Text>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Punch Out Time:</Text>
                <Text strong>{selectedDay.punchedOutAt ? dayjs(selectedDay.punchedOutAt).format('hh:mm A') : '--'}</Text>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Work Duration:</Text>
                <Text strong>
                  {selectedDay.totalDurationSeconds
                    ? `${Math.floor(selectedDay.totalDurationSeconds / 3600)}h ${Math.floor((selectedDay.totalDurationSeconds % 3600) / 60)}m`
                    : '--'}
                </Text>
              </div>

              {Number(selectedDay.breakSeconds || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Break Duration:</Text>
                  <Text>{Math.floor(selectedDay.breakSeconds / 60)} mins</Text>
                </div>
              )}

              {Number(selectedDay.lateMinutes || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Late Arrival:</Text>
                  <Tag color="volcano">{selectedDay.lateMinutes} mins late</Tag>
                </div>
              )}

              {Number(selectedDay.overtimeSeconds || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Overtime:</Text>
                  <Tag color="green">+{(Number(selectedDay.overtimeSeconds) / 3600).toFixed(1)} hrs</Tag>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* 4. MODAL: Apply Attendance Regularization */}
      <Modal
        title="Apply for Attendance Regularization"
        open={regModalVisible}
        onCancel={() => setRegModalVisible(false)}
        onOk={() => regForm.submit()}
        confirmLoading={submittingReg}
        okText="Submit Request"
      >
        <Form
          form={regForm}
          layout="vertical"
          onFinish={handleApplyRegularization}
          initialValues={{
            inTime: dayjs('09:30:00', 'HH:mm:ss'),
            outTime: dayjs('18:30:00', 'HH:mm:ss')
          }}
        >
          <Form.Item
            name="date"
            label="Date to Regularize"
            rules={[{ required: true, message: 'Please select the date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="inTime"
                label="Requested Punch In Time"
              >
                <TimePicker style={{ width: '100%' }} format="hh:mm A" use12Hours />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="outTime"
                label="Requested Punch Out Time"
              >
                <TimePicker style={{ width: '100%' }} format="hh:mm A" use12Hours />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="Reason for Regularization"
            rules={[{ required: true, message: 'Please specify the reason' }]}
          >
            <TextArea
              rows={3}
              placeholder="e.g. Forgot to punch in due to urgent onsite client visit, or biometric device glitch"
            />
          </Form.Item>
        </Form>
      </Modal>
    </StaffLayout>
  );
};

export default StaffAttendance;
